import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Chip, Link } from '@mui/material';
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import Panel from './crt/Panel';

export interface CalendarEvent {
  date: string;            // YYYY-MM-DD
  title: string;
  color: string;           // hex or css var
  type: string;            // legend key (e.g. "short", "vod")
  url?: string;
  subtitle?: string;
}

export interface LegendEntry {
  type: string;
  label: string;
  color: string;
}

interface Props {
  events: CalendarEvent[];
  legend: LegendEntry[];
  heading?: string;
}

// Jeeves-style day cell — green-on-black CRT palette reused from our theme
const CELL_MIN_HEIGHT = 118;

function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}

function monthLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase();
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function monthGridDays(monthStart: Date): Date[] {
  // Start on the Sunday on-or-before the 1st of the month.
  const first = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay()); // 0 = Sun
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  // Drop the trailing full week if we don't need it
  const lastOfMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  if (days[35].getMonth() !== monthStart.getMonth() && days[35] > lastOfMonth) {
    return days.slice(0, 35);
  }
  return days;
}

const ReleaseCalendar: React.FC<Props> = ({ events, legend, heading }) => {
  const [monthStart, setMonthStart] = useState(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });
  const [activeTypes, setActiveTypes] = useState<Set<string>>(
    () => new Set(legend.map((l) => l.type)),
  );

  const byDay = useMemo(() => {
    const map: Record<string, CalendarEvent[]> = {};
    for (const ev of events) {
      if (!activeTypes.has(ev.type)) continue;
      (map[ev.date] = map[ev.date] || []).push(ev);
    }
    return map;
  }, [events, activeTypes]);

  const days = useMemo(() => monthGridDays(monthStart), [monthStart]);
  const todayIso = isoDate(new Date());

  const eventsInMonth = useMemo(() => {
    const monthStr = isoDate(monthStart).slice(0, 7);
    return events.filter((e) => e.date.startsWith(monthStr) && activeTypes.has(e.type)).length;
  }, [events, monthStart, activeTypes]);

  const toggleType = (t: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  // Strip dashboards' decorative slashes from the heading so Panel can
  // re-apply its canonical "// LABEL" prefix on the border.
  const panelLabel = heading
    ? heading.replace(/^\s*\/\/\s*/, '').replace(/\s*\/\/\s*$/, '').trim()
    : 'RELEASE CALENDAR';

  return (
    <Panel label={panelLabel}>
      {/* Toolbar */}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={2}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setMonthStart(addMonths(monthStart, -1))}
          startIcon={<ChevronLeft />}
        >PREV</Button>
        <Typography
          variant="h6"
          sx={{
            minWidth: 220, textAlign: 'center',
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '13px', letterSpacing: '0.08em',
          }}
        >
          {monthLabel(monthStart)}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setMonthStart(addMonths(monthStart, 1))}
          endIcon={<ChevronRight />}
        >NEXT</Button>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            const t = new Date();
            setMonthStart(new Date(t.getFullYear(), t.getMonth(), 1));
          }}
          startIcon={<Today />}
        >TODAY</Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: '#5aaa5a', fontFamily: "'VT323', monospace", fontSize: '14px' }}>
          {eventsInMonth} events this month
        </Typography>
      </Box>

      {/* Legend — clickable to toggle visibility */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        {legend.map((l) => {
          const on = activeTypes.has(l.type);
          return (
            <Chip
              key={l.type}
              size="small"
              label={l.label}
              onClick={() => toggleType(l.type)}
              sx={{
                cursor: 'pointer',
                background: on ? `color-mix(in srgb, ${l.color} 14%, transparent)` : 'transparent',
                border: `1px solid ${on ? l.color : '#1e3a1e'}`,
                color: on ? l.color : '#5aaa5a',
                fontFamily: "'VT323', monospace",
                fontSize: '14px',
                '&:hover': { borderColor: l.color, color: l.color },
              }}
            />
          );
        })}
      </Box>

      {/* Weekday header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          background: '#1e3a1e',
          mb: '1px',
        }}
      >
        {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((w) => (
          <Box
            key={w}
            sx={{
              background: '#0a0a0a',
              py: 0.75,
              textAlign: 'center',
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '9px',
              color: '#5aaa5a',
              letterSpacing: '0.08em',
            }}
          >{w}</Box>
        ))}
      </Box>

      {/* Day grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '1px',
          background: '#1e3a1e',
        }}
      >
        {days.map((d, i) => {
          const iso = isoDate(d);
          const inMonth = d.getMonth() === monthStart.getMonth();
          const isToday = iso === todayIso;
          const cellEvents = byDay[iso] || [];
          return (
            <Box
              key={i}
              sx={{
                // Page-bg color (#0a0a0a) — opaque, matches surrounding
                // dashboard surface. The original opacity:0.35 dimming
                // for out-of-month cells (which let the CRT grid bleed
                // through) was moved off the parent onto the children
                // (see Typography color below + chip brightness filter).
                background: '#0a0a0a',
                minHeight: CELL_MIN_HEIGHT,
                p: 0.5,
                borderLeft: isToday ? '2px solid #00ff41' : 'none',
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontFamily: "'VT323', monospace",
                  fontSize: '14px',
                  // Out-of-month days dim via color, NOT parent opacity —
                  // opacity made the cell bg semi-transparent which let
                  // the CRT grid bleed through the cell.
                  color: isToday ? '#00ff41' : (inMonth ? '#5aaa5a' : '#2a4a2a'),
                  fontWeight: isToday ? 700 : 400,
                  mb: 0.25,
                }}
              >{d.getDate()}</Typography>
              {cellEvents.map((ev, idx) => {
                const body = (
                  <Box
                    key={idx}
                    title={ev.subtitle ? `${ev.title} — ${ev.subtitle}` : ev.title}
                    sx={{
                      display: 'block',
                      fontSize: '12px',
                      fontFamily: "'VT323', monospace",
                      lineHeight: 1.25,
                      px: '4px',
                      py: '2px',
                      mb: '2px',
                      borderLeft: `3px solid ${ev.color}`,
                      background: `color-mix(in srgb, ${ev.color} 12%, transparent)`,
                      color: ev.color,
                      // Dim event chips for out-of-month cells too (matches
                      // the date number's dimmed look).
                      filter: inMonth ? 'none' : 'brightness(0.45)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: ev.url ? 'pointer' : 'default',
                      textDecoration: 'none',
                      '&:hover': ev.url ? { filter: 'brightness(1.4)' } : undefined,
                    }}
                  >
                    {ev.title}
                  </Box>
                );
                return ev.url ? (
                  <Link key={idx} href={ev.url} target="_blank" rel="noopener" underline="none" sx={{ color: 'inherit' }}>
                    {body}
                  </Link>
                ) : body;
              })}
            </Box>
          );
        })}
      </Box>
    </Panel>
  );
};

export default ReleaseCalendar;
